/* Everything the book has recorded, as one dated list.
 *
 * The records already existed, each in its own place: trades at the bottom of
 * Taxes, dividends on their screen, money in and out under settings, contracts
 * on each contract. Every one of them answers "what is this record", and none
 * answers "what did I do in March", which is the question a broker's history
 * screen is for. This file puts them in one order and adds nothing to them.
 *
 * Pure, so the order and the filters can be tested without a screen. Nothing
 * here converts a currency: an entry keeps the currency it was recorded in,
 * because a list of past events restated at today's rate would print amounts
 * that never passed through any account. */

import type { BookData } from '../data/db';
import type { Account } from '../types';

export type ActivityKind =
  | 'buy' | 'sell' | 'dividend' | 'deposit' | 'withdrawal' | 'conversion' | 'transfer'
  | 'contract_open' | 'contract_close';

export type ActivityFilter = 'all' | 'trades' | 'dividends' | 'cash' | 'contracts';

export interface ActivityItem {
  /** Unique across every kind, so a list can key on it. */
  id: string;
  kind: ActivityKind;
  /** ISO datetime. Records kept as a bare date sort as that day's noon, the
      same hour the forms write. */
  at: string;
  day: string;
  ticker?: string | undefined;
  account?: Account | undefined;
  /** The other side of a conversion or a transfer. */
  toAccount?: Account | undefined;
  shares?: number | undefined;
  /** Per share: the fill, the dividend rate, or a contract's entry. */
  price?: number | undefined;
  /** Signed, in `currency`: what the event did to cash, or a contract's result. */
  amount?: number | undefined;
  currency: string;
  fee?: number | undefined;
  /** Tax withheld at source on a dividend. */
  tax?: number | undefined;
  realizedGain?: number | undefined;
  toAmount?: number | undefined;
  toCurrency?: string | undefined;
  side?: 'long' | 'short' | undefined;
  leverage?: number | undefined;
  /** Fraction of what was open that a contract close took, 0 to 1. */
  portion?: number | undefined;
  /** The close that ended the contract, as against a partial one. */
  final?: boolean | undefined;
  note?: string | undefined;
  /** Where the record lives, so the screen can open it there. */
  source: { type: 'trade' | 'dividend' | 'cashflow' | 'conversion' | 'transfer' | 'perp'; id: string };
}

export type ActivitySource = Pick<
  BookData, 'trades' | 'dividends' | 'cashflows' | 'conversions' | 'transfers' | 'perps' | 'holdings'
>;

const noon = (iso: string) => (iso.length === 10 ? `${iso}T12:00:00.000Z` : iso);

export function activityFeed(data: ActivitySource): ActivityItem[] {
  const out: ActivityItem[] = [];

  /* A trade stores its price in the listing's currency without naming it. The
     lot names it; a name sold out of entirely has no lot left, and falls back
     to dollars, which is what the trade form assumes for the same case. */
  const listing = new Map<string, string>();
  for (const h of data.holdings) if (!listing.has(h.ticker)) listing.set(h.ticker, h.currency);

  for (const t of data.trades) {
    const gross = t.shares * t.price;
    const fee = t.fee ?? 0;
    out.push({
      id: `trade:${t.id}`, kind: t.side, at: noon(t.at), day: t.at.slice(0, 10),
      ticker: t.ticker, account: t.account, shares: t.shares, price: t.price,
      // Out for a buy and in for a sale, net of what the broker took.
      amount: t.side === 'buy' ? -(gross + fee) : gross - fee,
      currency: listing.get(t.ticker) ?? 'USD',
      fee: fee > 0 ? fee : undefined,
      realizedGain: t.realizedGain,
      source: { type: 'trade', id: t.id },
    });
  }

  for (const d of data.dividends) {
    out.push({
      id: `dividend:${d.id}`, kind: 'dividend', at: noon(d.payDate), day: d.payDate.slice(0, 10),
      ticker: d.ticker, account: d.account, shares: d.shares, price: d.perShare,
      amount: d.net, currency: d.currency,
      tax: d.taxWithheld > 0 ? d.taxWithheld : undefined,
      note: d.note,
      source: { type: 'dividend', id: d.id },
    });
  }

  for (const f of data.cashflows) {
    out.push({
      id: `cashflow:${f.id}`, kind: f.amount >= 0 ? 'deposit' : 'withdrawal',
      at: noon(f.at), day: f.at.slice(0, 10),
      account: f.account, amount: f.amount, currency: f.currency, note: f.note,
      source: { type: 'cashflow', id: f.id },
    });
  }

  for (const c of data.conversions) {
    out.push({
      id: `conversion:${c.id}`, kind: 'conversion', at: noon(c.at), day: c.at.slice(0, 10),
      account: c.fromAccount, toAccount: c.toAccount,
      amount: -c.fromAmount, currency: c.fromCurrency,
      toAmount: c.toAmount, toCurrency: c.toCurrency, note: c.note,
      source: { type: 'conversion', id: c.id },
    });
  }

  for (const s of data.transfers) {
    out.push({
      id: `transfer:${s.id}`, kind: 'transfer', at: noon(s.at), day: s.at.slice(0, 10),
      ticker: s.ticker, account: s.fromAccount, toAccount: s.toAccount,
      shares: s.shares, currency: s.currency, note: s.note,
      source: { type: 'transfer', id: s.id },
    });
  }

  for (const p of data.perps) {
    /* No opening row for a contract recorded before opening dates were kept.
       Putting it on the day it was entered would date a trade to when it was
       typed in. */
    if (p.openedAt) {
      out.push({
        id: `perp:${p.id}:open`, kind: 'contract_open', at: noon(p.openedAt), day: p.openedAt.slice(0, 10),
        ticker: p.display, price: p.entryPrice, amount: p.marginUsdt, currency: 'USDT',
        side: p.side, leverage: p.leverage,
        source: { type: 'perp', id: p.id },
      });
    }
    // The same reading as `state/realised.ts`: the dated closes when there are
    // any, which include the final one, and the closing figure only otherwise.
    if (p.closes?.length) {
      p.closes.forEach((c, i) => {
        out.push({
          id: `perp:${p.id}:close:${i}`, kind: 'contract_close', at: noon(c.at), day: c.at.slice(0, 10),
          ticker: p.display, amount: c.resultUsdt, currency: 'USDT', portion: c.portion,
          final: Boolean(p.closedAt) && i === p.closes!.length - 1,
          source: { type: 'perp', id: p.id },
        });
      });
    } else if (p.closedAt && typeof p.closedResultUsdt === 'number') {
      out.push({
        id: `perp:${p.id}:close`, kind: 'contract_close', at: noon(p.closedAt), day: p.closedAt.slice(0, 10),
        ticker: p.display, amount: p.closedResultUsdt, currency: 'USDT', final: true,
        source: { type: 'perp', id: p.id },
      });
    }
  }

  return out.sort(newestFirst);
}

/* The order things happen in when the record cannot say.
 *
 * Every form writes a day and nothing finer, so two trades entered for the same
 * day carry the same moment and used to fall into whatever order their ids
 * happened to sort in — a purchase listed as happening before the sale that paid
 * for it. Money has to arrive before it can be spent, so among entries of one
 * moment the ones that bring money in go first, the ones that spend it after,
 * and money taken out of the book last. Lower happens earlier.
 *
 * Only a tie-break: an entry that does carry a real time, such as a broker's
 * import, is placed by that time. And it orders the list; it does not restate
 * any record. */
const HAPPENS: Record<ActivityKind, number> = {
  deposit: 0,
  dividend: 1,
  contract_close: 1,
  sell: 2,
  conversion: 3,
  transfer: 4,
  buy: 5,
  contract_open: 5,
  withdrawal: 6,
};

/** Newest first; within one moment, what happened later first. */
export function newestFirst(a: ActivityItem, b: ActivityItem): number {
  return b.at.localeCompare(a.at) || HAPPENS[b.kind] - HAPPENS[a.kind] || a.id.localeCompare(b.id);
}

const KINDS: Record<Exclude<ActivityFilter, 'all'>, ActivityKind[]> = {
  // A transfer moves shares, so it sits with the trades rather than the cash.
  trades: ['buy', 'sell', 'transfer'],
  dividends: ['dividend'],
  cash: ['deposit', 'withdrawal', 'conversion'],
  contracts: ['contract_open', 'contract_close'],
};

/** Narrow by kind, then by a ticker, an account's name or a note. */
export function filterActivity(
  items: ActivityItem[],
  filter: ActivityFilter,
  query: string,
  nameOf: (account: Account) => string = (a) => a,
): ActivityItem[] {
  const kinds = filter === 'all' ? null : new Set(KINDS[filter]);
  const q = query.trim().toLowerCase();
  return items.filter((i) => {
    if (kinds && !kinds.has(i.kind)) return false;
    if (q === '') return true;
    return [i.ticker, i.account && nameOf(i.account), i.toAccount && nameOf(i.toAccount), i.note]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(q);
  });
}

/** Runs of one day, in the order given. */
export function groupByDay(items: ActivityItem[]): Array<{ day: string; items: ActivityItem[] }> {
  const groups: Array<{ day: string; items: ActivityItem[] }> = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    if (last && last.day === item.day) last.items.push(item);
    else groups.push({ day: item.day, items: [item] });
  }
  return groups;
}

/** "Today", "Yesterday", or the date with its weekday — and the year once it
    is not this one. */
export function dayLabel(day: string, now = new Date()): string {
  if (day === now.toISOString().slice(0, 10)) return 'Today';
  if (day === new Date(now.getTime() - 86_400_000).toISOString().slice(0, 10)) return 'Yesterday';
  const d = new Date(`${day}T00:00:00Z`);
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'short', day: 'numeric', month: 'long', timeZone: 'UTC',
  };
  if (d.getUTCFullYear() !== now.getUTCFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('en-GB', opts);
}

const signTone = (v: number | undefined): 'up' | 'down' | undefined =>
  typeof v !== 'number' || v === 0 ? undefined : v > 0 ? 'up' : 'down';

/** Which way an entry's amount is coloured. Money that came in reads green and
    a loss taken reads red; anything that only turned money into something else
    — a purchase, a withdrawal, a conversion, shares moved — stays plain. A sale
    goes by what it realised, not by its proceeds, which are positive whether it
    made money or lost it; with no gain recorded it stays plain rather than
    guessing. The sign and the words beside the figure say the same, so colour
    is never the only signal (§11). */
export function amountTone(item: ActivityItem): 'up' | 'down' | undefined {
  switch (item.kind) {
    case 'deposit':
    case 'dividend':
      return 'up';
    case 'sell':
      return signTone(item.realizedGain);
    case 'contract_close':
      return signTone(item.amount);
    default:
      return undefined;
  }
}

/** Entries that moved money into, out of or between these accounts — either end
    counts, so a transfer from MEXC to a bank shows as MEXC's. Deposits,
    withdrawals and currency moves only: what a venue's own money did. */
export function moneyThrough(items: ActivityItem[], accounts: ReadonlySet<Account>): ActivityItem[] {
  return items.filter((i) =>
    (i.kind === 'deposit' || i.kind === 'withdrawal' || i.kind === 'conversion')
    && ((i.account !== undefined && accounts.has(i.account))
      || (i.toAccount !== undefined && accounts.has(i.toAccount))));
}
